import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion } from 'framer-motion';
import { Palette, ChevronDown, ChevronUp, Minus, Plus, X } from 'lucide-react';
import { frameService } from '../../services/frameService';
import { useAuthStore } from '../../stores/authStore';
import { useStorageUrl } from '../../hooks/useStorageUrl';
import {
  clampFrameOffset,
  formatFrameOffset,
  FRAME_OFFSET_MAX,
  FRAME_OFFSET_MIN,
  FRAME_OFFSET_STEP,
  getFrameMatOptions,
  normalizeMatColorHex,
  resolveFrameMatOption,
} from '../../lib/frameCustomization';
import type { Frame, FrameFamily, MatOption } from '@shared/types/firestore';

interface FrameSelectorProps {
  onFrameSelect: (
    frame: Frame | null,
    matColor?: MatOption,
    frameOffset?: number
  ) => void;
  onClose?: () => void;
  selectedFrame?: Frame | null;
  selectedMatColor?: MatOption | null;
  selectedFrameOffset?: number;
  expandedFamily: string | null;
  onExpandedFamilyChange: (familyId: string | null) => void;
  scrollAnchorId: string | null;
  onScrollAnchorChange: (anchorId: string | null) => void;
}

export default function FrameSelector({
  onFrameSelect,
  onClose,
  selectedFrame,
  selectedMatColor,
  selectedFrameOffset = 0,
  expandedFamily,
  onExpandedFamilyChange,
  scrollAnchorId,
  onScrollAnchorChange
}: FrameSelectorProps) {
  const { galleryId } = useAuthStore();
  const [frameFamilies, setFrameFamilies] = useState<FrameFamily[]>([]);
  const [frames, setFrames] = useState<Record<string, Frame[]>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [draftFrameOffset, setDraftFrameOffset] = useState(() => clampFrameOffset(selectedFrameOffset));
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const isRestoringScrollRef = useRef(false);
  const draftFrameOffsetRef = useRef(clampFrameOffset(selectedFrameOffset));
  const selectedFrameIdRef = useRef(selectedFrame?.id);
  const lastCommittedOffsetRef = useRef(clampFrameOffset(selectedFrameOffset));

  // Frame service is available as singleton

  const loadFramesInFamily = useCallback(async (familyId: string) => {
    if (!galleryId) return;

    if (familyId === 'no-frame') {
      setFrames(prev => ({
        ...prev,
        [familyId]: []
      }));
      return;
    }

    try {
      const familyFrames = await frameService.loadFramesInFamily(galleryId, familyId);
      setFrames(prev => ({
        ...prev,
        [familyId]: familyFrames
      }));
    } catch (err) {
      console.error('Error loading frames for family:', familyId, err);
    }
  }, [galleryId]);

  const loadFrameFamilies = useCallback(async () => {
    if (!galleryId) return;

    try {
      setLoading(true);
      const families = await frameService.loadFrameFamilies(galleryId);
      
      // Create a default "No Frame" family that always appears first
      const noFrameFamily: FrameFamily = {
        id: 'no-frame',
        name: 'No Frame',
        description: 'View artwork without any framing',
        active: true,
        sortOrder: -1, // Ensures it appears first
        thumbnailPath: null,
        createdDateUTC: new Date(),
        modifiedDateUTC: new Date()
      };
      
      // Add the no-frame family at the beginning
      const allFamilies = [noFrameFamily, ...families];
      setFrameFamilies(allFamilies);

      setFrames(prev => ({
        ...prev,
        'no-frame': []
      }));
    } catch (err) {
      console.error('Error loading frame families:', err);
      setError('Failed to load frame families');
    } finally {
      setLoading(false);
    }
  }, [galleryId]);

  useEffect(() => {
    if (galleryId) {
      loadFrameFamilies();
    }
  }, [galleryId, loadFrameFamilies]);

  useEffect(() => {
    if (loading || expandedFamily || frameFamilies.length === 0) return;

    onExpandedFamilyChange(selectedFrame?.familyId ?? 'no-frame');
  }, [expandedFamily, frameFamilies.length, loading, onExpandedFamilyChange, selectedFrame?.familyId]);

  const handleFamilyToggle = (familyId: string) => {
    if (expandedFamily === familyId) {
      onExpandedFamilyChange(null);
    } else {
      onExpandedFamilyChange(familyId);
      if (!frames[familyId]) {
        loadFramesInFamily(familyId);
      }
    }
  };

  const handleFrameSelect = (frame: Frame) => {
    const defaultMat = resolveFrameMatOption(frame);
    setDraftFrameOffset(0);
    draftFrameOffsetRef.current = 0;
    lastCommittedOffsetRef.current = 0;
    onScrollAnchorChange(frameMatAnchorId(frame.id!));
    onFrameSelect(frame, defaultMat, 0);
  };

  const handleMatColorSelect = (matColor: MatOption) => {
    if (selectedFrame) {
      onFrameSelect(selectedFrame, matColor, draftFrameOffset);
    }
  };

  const handleOffsetChange = (value: number) => {
    if (!selectedFrame) return;

    const nextOffset = clampFrameOffset(value);
    setDraftFrameOffset(nextOffset);
    draftFrameOffsetRef.current = nextOffset;
    lastCommittedOffsetRef.current = nextOffset;
    onFrameSelect(
      selectedFrame,
      selectedMatColor ?? resolveFrameMatOption(selectedFrame),
      nextOffset
    );
  };

  const commitDraftOffset = () => {
    const nextOffset = draftFrameOffsetRef.current;
    if (nextOffset === lastCommittedOffsetRef.current) return;
    handleOffsetChange(nextOffset);
  };

  const handleRemoveFrame = () => {
    onScrollAnchorChange(frameAnchorId('no-frame'));
    setDraftFrameOffset(0);
    draftFrameOffsetRef.current = 0;
    lastCommittedOffsetRef.current = 0;
    onFrameSelect(null, undefined, 0);
  };

  useEffect(() => {
    if (selectedFrame?.id === selectedFrameIdRef.current) return;

    const nextOffset = clampFrameOffset(selectedFrameOffset);
    selectedFrameIdRef.current = selectedFrame?.id;
    draftFrameOffsetRef.current = nextOffset;
    lastCommittedOffsetRef.current = nextOffset;
    setDraftFrameOffset(nextOffset);
  }, [selectedFrame?.id, selectedFrameOffset]);

  const updateScrollAnchor = () => {
    if (isRestoringScrollRef.current) return;

    const container = scrollContainerRef.current;
    if (!container) return;

    const anchors = Array.from(container.querySelectorAll<HTMLElement>('[data-frame-anchor]'));
    const containerTop = container.getBoundingClientRect().top;
    const topTolerance = 24;
    let nearestPastTop: { id: string; offset: number } | null = null;
    let nearestFutureTop: { id: string; offset: number } | null = null;

    anchors.forEach(anchor => {
      const id = anchor.dataset.frameAnchor;
      if (!id) return;

      const offset = anchor.getBoundingClientRect().top - containerTop;
      if (offset <= topTolerance) {
        if (!nearestPastTop || offset > nearestPastTop.offset) {
          nearestPastTop = { id, offset };
        }
        return;
      }

      if (!nearestFutureTop || offset < nearestFutureTop.offset) {
        nearestFutureTop = { id, offset };
      }
    });

    onScrollAnchorChange(nearestPastTop?.id ?? nearestFutureTop?.id ?? null);
  };

  const restoreScrollAnchor = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container || !scrollAnchorId) return;

    const target = container.querySelector<HTMLElement>(`[data-frame-anchor="${CSS.escape(scrollAnchorId)}"]`);
    if (!target) return;

    isRestoringScrollRef.current = true;
    target.scrollIntoView({ block: 'start' });
    window.requestAnimationFrame(() => {
      isRestoringScrollRef.current = false;
    });
  }, [scrollAnchorId]);

  useEffect(() => {
    if (!expandedFamily) return;

    void loadFramesInFamily(expandedFamily);
  }, [expandedFamily, loadFramesInFamily]);

  useEffect(() => {
    const frameId = window.requestAnimationFrame(restoreScrollAnchor);
    return () => window.cancelAnimationFrame(frameId);
  }, [expandedFamily, frameFamilies.length, frames, restoreScrollAnchor, selectedFrame?.id]);

  const frameFamilyAnchorId = (familyId: string) => `frame-family-${familyId}`;
  const frameAnchorId = (frameId: string) => `frame-${frameId}`;
  const frameMatAnchorId = (frameId: string) => `frame-mat-${frameId}`;

  if (loading) {
    return (
      <div className="w-80 bg-white border-l border-gray-200 p-4">
        <div className="flex items-center justify-center h-32">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="w-80 bg-white border-l border-gray-200 p-4">
        <div className="text-red-600 text-sm">{error}</div>
      </div>
    );
  }

  const selectedMatColorHex = normalizeMatColorHex(selectedMatColor?.hexColor);
  const availableMatOptions = selectedFrame ? getFrameMatOptions(selectedFrame) : [];
  if (
    selectedFrame &&
    selectedMatColor &&
    !availableMatOptions.some(option => option.hexColor === selectedMatColorHex)
  ) {
    availableMatOptions.push({ ...selectedMatColor, hexColor: selectedMatColorHex });
  }

  return (
    <div className="w-80 bg-white border-l border-gray-200 flex flex-col max-h-full">
      {/* Header */}
      <div className="p-4 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <Palette className="w-5 h-5 text-blue-600" />
            <h3 className="font-medium text-gray-900">Frame Selection</h3>
          </div>
          {onClose && (
            <button
              onClick={onClose}
              className="p-1 rounded-lg hover:bg-gray-100 transition-colors"
              title="Close frame selector"
            >
              <X className="w-5 h-5 text-gray-500" />
            </button>
          )}
        </div>
      </div>

      {/* Content */}
      <div ref={scrollContainerRef} onScroll={updateScrollAnchor} className="flex-1 overflow-y-auto p-4 space-y-4">

        {/* Frame Families */}
        {frameFamilies.map((family) => (
          <div
            key={family.id}
            className="border border-gray-200 rounded-lg overflow-hidden"
            data-frame-anchor={frameFamilyAnchorId(family.id)}
          >
            {/* Family Header */}
            <button
              onClick={() => handleFamilyToggle(family.id)}
              className="w-full p-3 bg-gray-50 hover:bg-gray-100 transition-colors text-left flex items-center justify-between"
            >
              <div>
                <div className="font-medium text-gray-900">{family.name}</div>
                {family.description && (
                  <div className="text-sm text-gray-500 mt-1">{family.description}</div>
                )}
              </div>
              {expandedFamily === family.id ? (
                <ChevronUp className="w-4 h-4 text-gray-500" />
              ) : (
                <ChevronDown className="w-4 h-4 text-gray-500" />
              )}
            </button>

            {/* Family Frames */}
            {expandedFamily === family.id && (
              <motion.div
                initial={{ height: 0 }}
                animate={{ height: 'auto' }}
                exit={{ height: 0 }}
                transition={{ duration: 0.2 }}
                className="overflow-hidden"
              >
                <div className="p-3 space-y-2 bg-white">
                  {family.id === 'no-frame' ? (
                    /* Special "No Frame" option */
                    <button
                      onClick={handleRemoveFrame}
                      className={`w-full p-3 border rounded-lg text-left transition-colors ${
                        !selectedFrame 
                          ? 'border-blue-500 bg-blue-50 text-blue-700' 
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                      data-frame-anchor={frameAnchorId('no-frame')}
                    >
                      <div className="font-medium">No Frame</div>
                      <div className="text-sm text-gray-500">View artwork without framing</div>
                    </button>
                  ) : (
                    /* Regular frames */
                    <>
                      {frames[family.id]?.map((frame) => (
                        <FrameItem
                          key={frame.id}
                          frame={frame}
                          isSelected={selectedFrame?.id === frame.id}
                          anchorId={frameAnchorId(frame.id!)}
                          onSelect={() => {
                            handleFrameSelect(frame);
                          }}
                        />
                      ))}
                      {!frames[family.id] && (
                        <div className="text-center py-4 text-gray-500">
                          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mx-auto"></div>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </motion.div>
            )}
          </div>
        ))}

        {/* Mat customization */}
        {selectedFrame && (
          <div className="border border-gray-200 rounded-lg p-3" data-frame-anchor={frameMatAnchorId(selectedFrame.id!)}>
            <h4 className="font-medium text-gray-900 mb-3">Mat</h4>
            <div className="grid grid-cols-2 gap-2">
              {availableMatOptions.map((matOption) => (
                <button
                  key={`${matOption.id}-${matOption.hexColor}`}
                  onClick={() => handleMatColorSelect(matOption)}
                  className={`min-w-0 rounded-lg border p-2 transition-colors ${
                    selectedMatColorHex === matOption.hexColor
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                  }`}
                  title={matOption.name}
                  aria-label={`Use ${matOption.name} mat`}
                >
                  <span
                    className={`block h-10 w-full rounded-md border ${
                      matOption.hexColor === '#FFFFFF' ? 'border-gray-300' : 'border-black/10'
                    }`}
                    style={{ backgroundColor: matOption.hexColor }}
                  />
                  <span className="mt-1.5 block truncate text-xs font-medium text-gray-700">
                    {matOption.name}
                  </span>
                </button>
              ))}
            </div>

            <div className="my-4 border-t border-gray-200" />

            <div className="space-y-2">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <div className="text-sm font-medium text-gray-900">Frame Offset</div>
                  <div className="text-xs text-gray-500">Mat size</div>
                </div>
                <span className="shrink-0 text-xs font-medium tabular-nums text-gray-700">
                  {formatFrameOffset(draftFrameOffset)}
                </span>
              </div>

              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => handleOffsetChange(draftFrameOffset - FRAME_OFFSET_STEP)}
                  disabled={draftFrameOffset <= FRAME_OFFSET_MIN}
                  className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-gray-300 text-gray-700 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40"
                  title="Decrease mat size"
                  aria-label="Decrease mat size"
                >
                  <Minus className="h-4 w-4" />
                </button>

                <input
                  type="range"
                  min={FRAME_OFFSET_MIN}
                  max={FRAME_OFFSET_MAX}
                  step={FRAME_OFFSET_STEP}
                  value={draftFrameOffset}
                  onChange={event => {
                    const nextOffset = clampFrameOffset(Number(event.target.value));
                    draftFrameOffsetRef.current = nextOffset;
                    setDraftFrameOffset(nextOffset);
                  }}
                  onPointerUp={commitDraftOffset}
                  onPointerCancel={commitDraftOffset}
                  onKeyUp={commitDraftOffset}
                  onBlur={commitDraftOffset}
                  className="min-w-0 flex-1 accent-blue-600"
                  aria-label="Mat size offset"
                  aria-valuetext={formatFrameOffset(draftFrameOffset)}
                />

                <button
                  type="button"
                  onClick={() => handleOffsetChange(draftFrameOffset + FRAME_OFFSET_STEP)}
                  disabled={draftFrameOffset >= FRAME_OFFSET_MAX}
                  className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-gray-300 text-gray-700 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40"
                  title="Increase mat size"
                  aria-label="Increase mat size"
                >
                  <Plus className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

interface FrameItemProps {
  frame: Frame;
  isSelected: boolean;
  anchorId: string;
  onSelect: () => void;
}

function FrameItem({ frame, isSelected, anchorId, onSelect }: FrameItemProps) {
  const { url: thumbnailUrl } = useStorageUrl(frame.thumbnailPath);

  return (
    <button
      onClick={onSelect}
      className={`w-full p-2 border rounded-lg text-left transition-colors flex items-center space-x-3 ${
        isSelected 
          ? 'border-blue-500 bg-blue-50' 
          : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
      }`}
      data-frame-anchor={anchorId}
    >
      {/* Frame Thumbnail */}
      <div className="w-12 h-12 bg-gray-100 rounded overflow-hidden flex-shrink-0">
        {thumbnailUrl ? (
          <img
            src={thumbnailUrl}
            alt={frame.name}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Palette className="w-4 h-4 text-gray-400" />
          </div>
        )}
      </div>

      {/* Frame Info */}
      <div className="flex-1 min-w-0">
        <div className="font-medium text-gray-900 truncate">{frame.name}</div>
        <div className="text-sm text-gray-500 truncate">
          {frame.style} • {frame.material}
        </div>
        {frame.price && (
          <div className="text-sm text-green-600">${frame.price}</div>
        )}
      </div>
    </button>
  );
}
